import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, Plus, Trash2, BarChart3, Award, Shield, MapPin,
  IndianRupee, Home, CheckCircle, XCircle, ArrowRight, Star, Pencil
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";

const formatINR = (v) => {
  if (!v) return "N/A";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const emptyExtProject = {
  name: "", developer: "", location: "", price_min: "", price_max: "", price_per_sqft: "",
  configurations: "", amenities: "", possession_date: "", rera_number: "", rera_compliant: true,
  connectivity: "", total_units: "", available_units: "",
};

const ProjectComparisonPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [trustScores, setTrustScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [extDialogOpen, setExtDialogOpen] = useState(false);
  const [extForm, setExtForm] = useState({ ...emptyExtProject });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...emptyExtProject });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const { canEdit } = usePermissions();

  useEffect(() => {
    axios.get(`${API}/projects`).then(res => { setProjects(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleSelect = (pid) => {
    setSelectedIds(prev =>
      prev.includes(pid) ? prev.filter(id => id !== pid) : prev.length < 4 ? [...prev, pid] : prev
    );
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) { toast.error("Select at least 2 projects"); return; }
    setComparing(true);
    try {
      const res = await axios.post(`${API}/decision-support/compare`, { project_ids: selectedIds });
      setComparison(res.data);
      // Fetch trust scores
      const scores = {};
      for (const pid of selectedIds) {
        try {
          const tRes = await axios.get(`${API}/decision-support/trust-score/${pid}`);
          scores[pid] = tRes.data;
        } catch {}
      }
      setTrustScores(scores);
    } catch (err) { toast.error("Comparison failed"); }
    finally { setComparing(false); }
  };

  const addExternalProject = async () => {
    if (!extForm.name.trim() || !extForm.location.trim()) { toast.error("Name and location required"); return; }
    try {
      const payload = {
        ...extForm,
        price_min: parseFloat(extForm.price_min) || 0,
        price_max: parseFloat(extForm.price_max) || 0,
        price_per_sqft: extForm.price_per_sqft ? parseFloat(extForm.price_per_sqft) : null,
        configurations: extForm.configurations ? extForm.configurations.split(",").map(s => s.trim()).filter(Boolean) : [],
        amenities: extForm.amenities ? extForm.amenities.split(",").map(s => s.trim()).filter(Boolean) : [],
        connectivity: extForm.connectivity ? extForm.connectivity.split(",").map(s => s.trim()).filter(Boolean) : [],
        total_units: extForm.total_units ? parseInt(extForm.total_units) : null,
        available_units: extForm.available_units ? parseInt(extForm.available_units) : null,
        is_external: true,
        status: "external",
      };
      const res = await axios.post(`${API}/projects`, payload);
      toast.success("External project added");
      setProjects([...projects, res.data]);
      setExtDialogOpen(false);
      setExtForm({ ...emptyExtProject });
    } catch (err) { toast.error("Failed to add project"); }
  };

  const openEditDialog = (e, project) => {
    e.stopPropagation();
    setEditingProjectId(project.project_id);
    setEditForm({
      name: project.name || "",
      developer: project.developer || "",
      location: project.location || "",
      price_min: project.price_min || "",
      price_max: project.price_max || "",
      price_per_sqft: project.price_per_sqft || "",
      configurations: Array.isArray(project.configurations) ? project.configurations.join(", ") : "",
      amenities: Array.isArray(project.amenities) ? project.amenities.join(", ") : "",
      possession_date: project.possession_date || "",
      rera_number: project.rera_number || "",
      rera_compliant: project.rera_compliant ?? true,
      connectivity: Array.isArray(project.connectivity) ? project.connectivity.join(", ") : "",
      total_units: project.total_units || "",
      available_units: project.available_units || "",
    });
    setEditDialogOpen(true);
  };

  const saveEditProject = async () => {
    if (!editForm.name.trim() || !editForm.location.trim()) { toast.error("Name and location required"); return; }
    try {
      const payload = {
        name: editForm.name,
        developer: editForm.developer,
        location: editForm.location,
        price_min: parseFloat(editForm.price_min) || 0,
        price_max: parseFloat(editForm.price_max) || 0,
        price_per_sqft: editForm.price_per_sqft ? parseFloat(editForm.price_per_sqft) : null,
        configurations: editForm.configurations ? editForm.configurations.split(",").map(s => s.trim()).filter(Boolean) : [],
        amenities: editForm.amenities ? editForm.amenities.split(",").map(s => s.trim()).filter(Boolean) : [],
        connectivity: editForm.connectivity ? editForm.connectivity.split(",").map(s => s.trim()).filter(Boolean) : [],
        total_units: editForm.total_units ? parseInt(editForm.total_units) : null,
        available_units: editForm.available_units ? parseInt(editForm.available_units) : null,
        possession_date: editForm.possession_date,
        rera_number: editForm.rera_number,
        rera_compliant: editForm.rera_compliant,
      };
      const res = await axios.put(`${API}/projects/${editingProjectId}`, payload);
      toast.success("Project updated successfully");
      setProjects(projects.map(p => p.project_id === editingProjectId ? res.data : p));
      setEditDialogOpen(false);
    } catch (err) { toast.error("Failed to update project"); }
  };

  const confirmDeleteProject = (e, project) => {
    e.stopPropagation();
    setDeletingProject(project);
    setDeleteConfirmOpen(true);
  };

  const deleteExternalProject = async () => {
    if (!deletingProject) return;
    try {
      await axios.delete(`${API}/projects/${deletingProject.project_id}`);
      toast.success(`"${deletingProject.name}" deleted`);
      setProjects(projects.filter(p => p.project_id !== deletingProject.project_id));
      setSelectedIds(selectedIds.filter(id => id !== deletingProject.project_id));
      setDeleteConfirmOpen(false);
      setDeletingProject(null);
    } catch (err) { toast.error("Failed to delete project"); }
  };

  const getTrustColor = (label) => {
    const colors = { highly_trusted: "text-emerald-600", trusted: "text-blue-600", moderate: "text-amber-600", low_trust: "text-red-600" };
    return colors[label] || "text-slate-500";
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="project-comparison-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Project Comparison</h1>
            <p className="text-sm text-slate-500 mt-1">Compare projects side-by-side — including competitor projects</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setExtDialogOpen(true)} data-testid="add-external-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Add External Project
            </Button>
            <Button onClick={handleCompare} disabled={selectedIds.length < 2 || comparing} data-testid="compare-btn">
              <BarChart3 className="w-4 h-4 mr-2" /> Compare ({selectedIds.length})
            </Button>
          </div>
        </div>

        {/* Project Selection */}
        {!comparison && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {loading ? <p className="text-slate-400 col-span-3 text-center py-12">Loading projects...</p> :
            projects.length === 0 ? <p className="text-slate-400 col-span-3 text-center py-12">No projects found. Add your first project.</p> :
            projects.map((p) => {
              const selected = selectedIds.includes(p.project_id);
              return (
                <Card key={p.project_id}
                  className={`cursor-pointer transition-all ${selected ? "ring-2 ring-blue-500 shadow-md" : "hover:shadow-sm"}`}
                  onClick={() => toggleSelect(p.project_id)} data-testid={`project-select-${p.project_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{p.name}</p>
                          {p.is_external && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600">External</Badge>}
                        </div>
                        {p.developer && <p className="text-xs text-slate-500">by {p.developer}</p>}
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" /> {p.location}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.is_external && canEdit("edit_external_projects") && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => openEditDialog(e, p)} data-testid={`edit-external-project-${p.project_id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                        )}
                        {p.is_external && canEdit("delete_external_projects") && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => confirmDeleteProject(e, p)} data-testid={`delete-external-project-${p.project_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        )}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selected ? "bg-blue-500 border-blue-500" : "border-slate-300"}`}>
                          {selected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span className="text-slate-700 font-medium">{formatINR(p.price_min)} - {formatINR(p.price_max)}</span>
                      {p.configurations?.length > 0 && <span className="text-slate-400">{p.configurations.join(", ")}</span>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Comparison Results */}
        {comparison && (
          <div className="space-y-6" data-testid="comparison-results">
            <Button variant="outline" onClick={() => { setComparison(null); setTrustScores({}); }} data-testid="back-to-selection-btn">
              Back to Selection
            </Button>

            {/* Side by Side Cards */}
            <div className={`grid gap-4 ${comparison.projects.length === 2 ? "md:grid-cols-2" : comparison.projects.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
              {comparison.projects.map((p, i) => {
                const trust = trustScores[p.project_id];
                const isBestPrice = comparison.best_values.lowest_price === p.name;
                const isMostAmenities = comparison.best_values.most_amenities === p.name;
                return (
                  <Card key={i} className="relative" data-testid={`comparison-card-${i}`}>
                    {isBestPrice && <Badge className="absolute -top-2 right-2 bg-emerald-500 text-white text-xs">Best Price</Badge>}
                    {isMostAmenities && <Badge className="absolute -top-2 left-2 bg-blue-500 text-white text-xs">Most Amenities</Badge>}
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-slate-600" />
                        <div>
                          <CardTitle className="text-base">{p.name}</CardTitle>
                          {p.developer && <p className="text-xs text-slate-500">{p.developer}</p>}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div><p className="text-xs text-slate-500">Location</p><p className="font-medium">{p.location}</p></div>
                      <div><p className="text-xs text-slate-500">Price Range</p><p className="font-medium text-emerald-700">{formatINR(p.price_min)} - {formatINR(p.price_max)}</p></div>
                      {p.price_per_sqft && <div><p className="text-xs text-slate-500">Price/sq.ft</p><p className="font-medium">{formatINR(p.price_per_sqft)}</p></div>}
                      <div><p className="text-xs text-slate-500">Configurations</p><div className="flex flex-wrap gap-1 mt-1">{(p.configurations || []).map((c, j) => <Badge key={j} variant="secondary" className="text-xs">{c}</Badge>)}</div></div>
                      {p.possession_date && <div><p className="text-xs text-slate-500">Possession</p><p className="font-medium">{p.possession_date}</p></div>}
                      <div><p className="text-xs text-slate-500">RERA</p><p className="font-medium flex items-center gap-1">{p.rera_compliant ? <><CheckCircle className="w-3 h-3 text-emerald-500" /> {p.rera_number || "Yes"}</> : <><XCircle className="w-3 h-3 text-red-500" /> No</>}</p></div>
                      {p.amenities?.length > 0 && <div><p className="text-xs text-slate-500">Amenities ({p.amenities.length})</p><p className="text-xs text-slate-600 mt-1">{p.amenities.slice(0, 6).join(", ")}{p.amenities.length > 6 ? ` +${p.amenities.length - 6} more` : ""}</p></div>}
                      {p.connectivity?.length > 0 && <div><p className="text-xs text-slate-500">Connectivity</p><p className="text-xs text-slate-600 mt-1">{p.connectivity.join(", ")}</p></div>}
                      {p.available_units != null && p.total_units && (
                        <div><p className="text-xs text-slate-500">Availability</p><p className="font-medium">{p.available_units}/{p.total_units} units</p></div>
                      )}
                      {/* Trust Score */}
                      {trust && (
                        <div className="border-t pt-3 mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Trust Score</span>
                            <span className={`text-sm font-bold ${getTrustColor(trust.trust_label)}`}>{trust.trust_score}/100</span>
                          </div>
                          <Progress value={trust.trust_score} className="h-1.5" />
                          <p className={`text-xs mt-1 capitalize ${getTrustColor(trust.trust_label)}`}>{trust.trust_label?.replace(/_/g, " ")}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* External Project Dialog */}
        <Dialog open={extDialogOpen} onOpenChange={setExtDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="external-project-dialog">
            <DialogHeader><DialogTitle>Add External / Competitor Project</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Project Name *</label>
                  <Input value={extForm.name} onChange={(e) => setExtForm({ ...extForm, name: e.target.value })} placeholder="e.g. Godrej Garden City" data-testid="ext-name-input" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Developer</label>
                  <Input value={extForm.developer} onChange={(e) => setExtForm({ ...extForm, developer: e.target.value })} placeholder="e.g. Godrej Properties" /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Location *</label>
                <Input value={extForm.location} onChange={(e) => setExtForm({ ...extForm, location: e.target.value })} placeholder="e.g. Whitefield, Bangalore" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Price Min</label><Input type="number" value={extForm.price_min} onChange={(e) => setExtForm({ ...extForm, price_min: e.target.value })} placeholder="5000000" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Price Max</label><Input type="number" value={extForm.price_max} onChange={(e) => setExtForm({ ...extForm, price_max: e.target.value })} placeholder="10000000" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Per Sqft</label><Input type="number" value={extForm.price_per_sqft} onChange={(e) => setExtForm({ ...extForm, price_per_sqft: e.target.value })} placeholder="8500" /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Configurations</label><Input value={extForm.configurations} onChange={(e) => setExtForm({ ...extForm, configurations: e.target.value })} placeholder="1 BHK, 2 BHK, 3 BHK" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Amenities</label><Input value={extForm.amenities} onChange={(e) => setExtForm({ ...extForm, amenities: e.target.value })} placeholder="Pool, Gym, Clubhouse, Park" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Possession Date</label><Input value={extForm.possession_date} onChange={(e) => setExtForm({ ...extForm, possession_date: e.target.value })} placeholder="Dec 2027" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">RERA Number</label><Input value={extForm.rera_number} onChange={(e) => setExtForm({ ...extForm, rera_number: e.target.value })} placeholder="P52100012345" /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Connectivity</label><Input value={extForm.connectivity} onChange={(e) => setExtForm({ ...extForm, connectivity: e.target.value })} placeholder="Metro 2km, Airport 15km, IT Park 5km" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Total Units</label><Input type="number" value={extForm.total_units} onChange={(e) => setExtForm({ ...extForm, total_units: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Available Units</label><Input type="number" value={extForm.available_units} onChange={(e) => setExtForm({ ...extForm, available_units: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExtDialogOpen(false)}>Cancel</Button>
              <Button onClick={addExternalProject} data-testid="ext-save-btn">Add Project</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit External Project Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-external-project-dialog">
            <DialogHeader><DialogTitle>Edit External Project</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Project Name *</label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-ext-name-input" /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Developer</label>
                  <Input value={editForm.developer} onChange={(e) => setEditForm({ ...editForm, developer: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Location *</label>
                <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="edit-ext-location-input" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Price Min</label><Input type="number" value={editForm.price_min} onChange={(e) => setEditForm({ ...editForm, price_min: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Price Max</label><Input type="number" value={editForm.price_max} onChange={(e) => setEditForm({ ...editForm, price_max: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Per Sqft</label><Input type="number" value={editForm.price_per_sqft} onChange={(e) => setEditForm({ ...editForm, price_per_sqft: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Configurations</label><Input value={editForm.configurations} onChange={(e) => setEditForm({ ...editForm, configurations: e.target.value })} placeholder="1 BHK, 2 BHK, 3 BHK" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Amenities</label><Input value={editForm.amenities} onChange={(e) => setEditForm({ ...editForm, amenities: e.target.value })} placeholder="Pool, Gym, Clubhouse, Park" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Possession Date</label><Input value={editForm.possession_date} onChange={(e) => setEditForm({ ...editForm, possession_date: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">RERA Number</label><Input value={editForm.rera_number} onChange={(e) => setEditForm({ ...editForm, rera_number: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Connectivity</label><Input value={editForm.connectivity} onChange={(e) => setEditForm({ ...editForm, connectivity: e.target.value })} placeholder="Metro 2km, Airport 15km, IT Park 5km" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Total Units</label><Input type="number" value={editForm.total_units} onChange={(e) => setEditForm({ ...editForm, total_units: e.target.value })} /></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Available Units</label><Input type="number" value={editForm.available_units} onChange={(e) => setEditForm({ ...editForm, available_units: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveEditProject} data-testid="edit-ext-save-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md" data-testid="delete-external-project-dialog">
            <DialogHeader><DialogTitle>Delete External Project</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 py-2">
              Are you sure you want to delete <span className="font-semibold">{deletingProject?.name}</span>? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeletingProject(null); }}>Cancel</Button>
              <Button variant="destructive" onClick={deleteExternalProject} data-testid="confirm-delete-ext-btn">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProjectComparisonPage;
